// src/encounter/reported-date-field.tsx
// The rail's patient-reported date field: the shared `DynamicDateInput` (full date, month-year,
// year, or a duration as an input aid) plus an explicit "Unknown" choice, so an unknown date is
// recorded as such and is never confused with a field left blank. Encounter owner answers on PR
// #843 (1 Nov 2026). What is stored is decided by `resolveReportedDate` (lib/reported-date.ts).
// Used by the condition-onset and procedure-date fields of the rail "+" forms.
import { useId } from 'react';
import { Checkbox } from '@scripsteam/scrips-signal-ds';
import { DynamicDateInput } from '@/patients/components/chart/dynamic-date-input';
import { UNKNOWN_DATE_TEXT, type ReportedDateDraft } from './lib/reported-date';

export interface ReportedDateFieldProps {
  label: string;
  value: ReportedDateDraft;
  onChange: (next: ReportedDateDraft) => void;
  disabled?: boolean;
}

export function ReportedDateField({ label, value, onChange, disabled }: ReportedDateFieldProps) {
  const unknownId = useId();
  return (
    <div>
      <DynamicDateInput
        label={label}
        displayValue={value.unknown ? '' : value.displayValue}
        isoValue={value.unknown ? null : value.isoValue}
        onChange={(next) => onChange({ ...next, unknown: false, edited: true })}
        disabled={disabled || value.unknown}
      />
      <div className="mt-2">
        {/* The DS Checkbox draws its own label (no type or colour class in app code, left-rail target §5). */}
        <Checkbox
          id={unknownId}
          label={UNKNOWN_DATE_TEXT}
          checked={value.unknown}
          disabled={disabled}
          // Choosing Unknown clears the typed date, so the two can never be stored together.
          onCheckedChange={(checked) =>
            onChange({ displayValue: '', isoValue: null, unknown: checked === true, edited: true })
          }
        />
      </div>
    </div>
  );
}
